/**
 * Eingaben der Kategorie-Auswahl am DSV-Beleg (Spec 2026-10-17 „DSV-Beleg per Klick einer Kategorie
 * zuordnen“): welche Kategorien dem Nutzer angeboten werden und ob die Reichweite nachgefragt wird.
 * Beides haengt an keiner Komponente, damit `DokumenteTab` und `SpeditionTab` dieselbe Antwort geben.
 */
import { visibleDocTypes } from "./status.ts";
import { entfernenRechte } from "./dokumentEntfernen.ts";
import type { BelegKategorieEinstellung } from "../components/Drawer/DocsTab/BelegKategorieWahl.tsx";
import type { Order } from "./types.ts";

/**
 * „Sonstige“ steht am Ende der Auswahl und heisst wie der Abschnitt im Reiter Dokumente und die
 * Definition im Backend (`DEFINITIONEN`, `id: "sonstige"`).
 */
export const SONSTIGE_KATEGORIE: Readonly<{ id: string; label: string }> = {
  id: "sonstige",
  label: "Sonstige Dokumente",
};

/**
 * Die Kategorien, die der Nutzer einem Beleg dieser Bestellung zuweisen darf: die Checklistentypen in
 * Checklistenreihenfolge (gleiche Quelle und gleiche Bezeichnungen wie `Checkliste.tsx`), dann
 * „Sonstige“. Gefiltert mit der Upload-Regel (`entfernenRechte`: `manage_documents`, bei
 * Zahlungsbelegtypen ODER `manage_payments`) - der Server urteilt verbindlich, die Liste spart nur den
 * aussichtslosen Versuch. Typen ohne Upload (`manualOnly`) bleiben draussen, sie haben auch in der
 * Checkliste keinen Hochladen-Knopf.
 */
export function belegKategorien(
  o: Order,
  rechte: ReadonlySet<string>,
): ReadonlyArray<{ id: string; label: string }> {
  const typen = visibleDocTypes(o)
    .filter((t) => !t.manualOnly)
    .map((t) => ({ id: t.id, label: t.label }));
  return [...typen, SONSTIGE_KATEGORIE].filter((t) => entfernenRechte(t.id).some((recht) => rechte.has(recht)));
}

/** Eine verknuepfte Sendung, soweit sie hier zaehlt (`SendungsTreffer` passt strukturell). */
export interface SendungsBezug {
  dsvShipmentNo?: string | null;
  verknuepfteBestellungen: readonly unknown[];
}

/**
 * Sendungsbezug der Bestellung fuer die Reichweite einer Zuordnung:
 *
 * - `mitSendung`: die Bestellung haengt an einer Sendung. Ohne Rueckfrage gilt dann `sendung`, sonst
 *   `bestellung`.
 * - `sammelsendung`: die Sendung traegt mehr als eine Bestellung, `bestellungen` ist ihre Zahl. Nur
 *   dann fragt die Auswahl nach der Reichweite.
 *
 * Die Zahl ist das Groesste aus der Zaehlung im Bestellstand (Bestellungen mit derselben
 * Sendungsnummer, `Order.dsvShipmentNo` - wie `SpeditionTab`), der Mitgliederzahl der Elternzeile
 * (`shipmentParent.memberCount`) und den verknuepften Bestellungen der geladenen Sendung: jede Quelle
 * kann hinterherhinken (Bestellstand nicht vollstaendig, Sendung noch nicht nachgeladen).
 */
export function sendungsBezug(
  o: Order,
  bestellungen: readonly Order[] | undefined,
  sendung?: SendungsBezug | null,
): { mitSendung: boolean; sammelsendung: { bestellungen: number } | null } {
  const nummer = o.dsvShipmentNo ?? sendung?.dsvShipmentNo ?? o.shipmentParent?.dsvShipmentNo ?? null;
  if (!nummer) return { mitSendung: false, sammelsendung: null };
  const imBestand = bestellungen ? bestellungen.filter((b) => b.dsvShipmentNo === nummer).length : 0;
  const anzahl = Math.max(imBestand, o.shipmentParent?.memberCount ?? 0, sendung?.verknuepfteBestellungen.length ?? 0);
  return { mitSendung: true, sammelsendung: anzahl >= 2 ? { bestellungen: anzahl } : null };
}

/**
 * Die Eingaben der Auswahl fuer ALLE Belegzeilen eines Reiters. EINE Stelle fuer `DokumenteTab` und
 * `SpeditionTab`: beide rechnen Typliste, Sendungsbezug und N ueber dieselbe Funktion mit denselben
 * Quellen (auch der geladenen `sendung`) - sonst bekaeme dieselbe Bestellung je nach Reiter eine andere
 * Entscheidung, ob nach der Reichweite gefragt wird.
 */
export function belegKategorieEinstellung(
  o: Order,
  bestellungen: readonly Order[] | undefined,
  sendung: SendungsBezug | null | undefined,
  rechte: ReadonlySet<string>,
  onZugeordnet: () => void,
): BelegKategorieEinstellung {
  const { mitSendung, sammelsendung } = sendungsBezug(o, bestellungen, sendung);
  return { typen: belegKategorien(o, rechte), sammelsendung, mitSendung, onZugeordnet };
}
